<template>
  <section class="custom-wrapper">
    <div class="row mt-2 mb-3">
      <div class="col-12">
        <h5 class="mb-3">Company Overview</h5>
        
        <div class="mb-3">
          <label for="companyOverview" class="form-label">Company Overview</label>
          <textarea 
            id="companyOverview"
            v-model="companyOverview" 
            class="form-control" 
            rows="8"
            placeholder="Enter a detailed description of your company, services, and what makes you unique..."
            @input="changed"
          ></textarea>
          <div class="form-text">Provide a comprehensive overview of your business that will be displayed to potential customers.</div>
        </div>
        
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="daysOfService" class="form-label">Days of the Week Services are Offered</label>
            <Multiselect
              id="daysOfService"
              v-model="daysOfService"
              mode="tags"
              :options="daysOfWeekOptions"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="Select days of service"
              class="form-control custom-multiselect"
              @change="changed"
            />
            <div class="form-text">Select multiple days when services are offered</div>
          </div>

          <div class="col-md-6 mb-3">
            <label for="supportedInsurers" class="form-label">Supported Insurers</label>
            <Multiselect
              id="supportedInsurers"
              v-model="supportedInsurers"
              mode="tags"
              :options="availableInsurersOptions"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :loading="loading.availableInsurers"
              placeholder="Select supported insurers"
              class="form-control custom-multiselect"
              @change="changed"
            />
            <div class="form-text">Select multiple insurers that you work with</div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-3 mb-3">
            <label for="openTime" class="form-label">Open Time</label>
            <input
              type="time"
              id="openTime"
              v-model="openTime"
              class="form-control"
              @input="changed"
            />
          </div>

          <div class="col-md-3 mb-3">
            <label for="closeTime" class="form-label">Close Time</label>
            <input
              type="time"
              id="closeTime"
              v-model="closeTime"
              class="form-control"
              @input="changed"
            />
          </div>
        </div>
        
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="businessLicenseNumbers" class="form-label">Local/National Business License Numbers</label>
            <input 
              type="text"
              id="businessLicenseNumbers"
              v-model="businessLicenseNumbers" 
              class="form-control" 
              placeholder="e.g., 123456, 789012, 345678"
              @input="changed"
            />
            <div class="form-text">Enter license numbers separated by commas</div>
          </div>
          
          <div class="col-md-6 mb-3">
            <label for="postalCodesServiced" class="form-label">Postal Codes Serviced</label>
            <input
              type="text"
              id="postalCodesServiced"
              :value="servingPostalCodes"
              class="form-control"
              readonly
              placeholder="No postal codes configured"
            />
            <div class="form-text">Configured in the Serving tab</div>
          </div>
        </div>
        
        <div class="row">
          <div class="col-md-4 mb-3">
            <label for="trainedTechnicians" class="form-label">Number of Trained Technicians</label>
            <input 
              type="number" 
              id="trainedTechnicians"
              v-model="trainedTechnicians" 
              class="form-control" 
              min="0" 
              placeholder="e.g., 5"
              @input="changed"
            />
          </div>
          
          <div class="col-md-4 mb-3">
            <label for="mobileVehicles" class="form-label">Number of Mobile Vehicles</label>
            <input 
              type="number" 
              id="mobileVehicles"
              v-model="mobileVehicles" 
              class="form-control" 
              min="0" 
              placeholder="e.g., 3"
              @input="changed"
            />
          </div>
          
          <div class="col-md-4 mb-3">
            <label for="calibrationCenters" class="form-label">Number of Calibration Centers</label>
            <input 
              type="number" 
              id="calibrationCenters"
              v-model="calibrationCenters" 
              class="form-control" 
              min="0" 
              placeholder="e.g., 1"
              @input="changed"
            />
          </div>
        </div>
        
        <div class="mb-3">
          <label for="warranty" class="form-label">Warranty</label>
          <textarea 
            id="warranty"
            v-model="warranty" 
            class="form-control" 
            rows="3"
            placeholder="Enter warranty information..."
            @input="changed"
          ></textarea>
        </div>
        
        <div class="mb-3">
          <label for="paymentOptions" class="form-label">Payment Options/Discounts</label>
          <div class="mb-2">
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                id="net30Terms" 
                v-model="net30Terms"
                @change="changed"
              >
              <label class="form-check-label" for="net30Terms">
                Net 30 Terms
              </label>
            </div>
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                id="otherPaymentOptions" 
                v-model="hasOtherPaymentOptions"
                @change="changed"
              >
              <label class="form-check-label" for="otherPaymentOptions">
                Other Payment Options
              </label>
            </div>
          </div>
          <textarea 
            v-if="hasOtherPaymentOptions"
            id="customPaymentOptions"
            v-model="customPaymentOptions" 
            class="form-control" 
            rows="3"
            placeholder="Enter custom payment options or discounts..."
            @input="changed"
          ></textarea>
        </div>
        
        <!-- Business Photos Section -->
        <BusinessPhotoUpload :firm="firm" @changed="changed" />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { locationStore } from '@/stores/location'
import Multiselect from '@vueform/multiselect'
import BusinessPhotoUpload from './BusinessPhotoUpload.vue'

export default {
  name: 'ProfileOverview',
  components: {
    Multiselect,
    BusinessPhotoUpload
  },
  props: {
    firm: Object
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      daysOfWeekOptions: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
      ]
    }
  },
  // Profile data is loaded by parent component's getLists() method
  emits: ['changed'],
  computed: {
    ...mapState(locationStore, ['profileData', 'location', 'postalCodes', 'availableInsurers', 'loading']),
    launchpadFirmId() {
      return this.location?.firmId || this.launchpadFirmId
    },
    companyOverview: {
      get() {
        return this.profileData?.companyOverview || ''
      },
      set(value) {
        this.updateProfileField('companyOverview', value, this.launchpadFirmId)
      }
    },
    daysOfService: {
      get() {
        const value = this.profileData?.daysOfService
        if (Array.isArray(value)) {
          return value
        }
        // Convert comma-separated string to array for backward compatibility
        return value ? value.split(',').map(day => day.trim()) : []
      },
      set(value) {
        this.updateProfileField('daysOfService', value, this.launchpadFirmId)
      }
    },
    hoursOfOperation: {
      get() {
        return this.profileData?.hoursOfOperation || ''
      },
      set(value) {
        this.updateProfileField('hoursOfOperation', value, this.launchpadFirmId)
      }
    },
    openTime: {
      get() {
        return this.profileData?.openTime || ''
      },
      set(value) {
        this.updateProfileField('openTime', value, this.launchpadFirmId)
      }
    },
    closeTime: {
      get() {
        return this.profileData?.closeTime || ''
      },
      set(value) {
        this.updateProfileField('closeTime', value, this.launchpadFirmId)
      }
    },
    businessLicenseNumbers: {
      get() {
        return this.profileData?.businessLicenseNumbers || ''
      },
      set(value) {
        this.updateProfileField('businessLicenseNumbers', value, this.launchpadFirmId)
      }
    },
    servingPostalCodes() {
      return this.postalCodes.length > 0 ? this.postalCodes.join(', ') : ''
    },
    trainedTechnicians: {
      get() {
        return this.profileData?.trainedTechnicians || ''
      },
      set(value) {
        this.updateProfileField('trainedTechnicians', value, this.launchpadFirmId)
      }
    },
    mobileVehicles: {
      get() {
        return this.profileData?.mobileVehicles || ''
      },
      set(value) {
        this.updateProfileField('mobileVehicles', value, this.launchpadFirmId)
      }
    },
    calibrationCenters: {
      get() {
        return this.profileData?.calibrationCenters || ''
      },
      set(value) {
        this.updateProfileField('calibrationCenters', value, this.launchpadFirmId)
      }
    },
    warranty: {
      get() {
        return this.profileData?.warranty || ''
      },
      set(value) {
        this.updateProfileField('warranty', value, this.launchpadFirmId)
      }
    },
    net30Terms: {
      get() {
        return this.profileData?.net30Terms || false
      },
      set(value) {
        this.updateProfileField('net30Terms', value, this.launchpadFirmId)
      }
    },
    hasOtherPaymentOptions: {
      get() {
        return this.profileData?.hasOtherPaymentOptions || false
      },
      set(value) {
        this.updateProfileField('hasOtherPaymentOptions', value, this.launchpadFirmId)
      }
    },
    customPaymentOptions: {
      get() {
        return this.profileData?.customPaymentOptions || ''
      },
      set(value) {
        this.updateProfileField('customPaymentOptions', value, this.launchpadFirmId)
      }
    },
    supportedInsurers: {
      get() {
        const value = this.profileData?.supportedInsurers
        if (Array.isArray(value)) {
          return value
        }
        // Convert comma-separated string to array for backward compatibility
        return value ? value.split(',').map(insurer => insurer.trim()) : []
      },
      set(value) {
        this.updateProfileField('supportedInsurers', value, this.launchpadFirmId)
      }
    },
    availableInsurersOptions() {
      if (!this.availableInsurers || !Array.isArray(this.availableInsurers)) {
        return []
      }
      return this.availableInsurers.map(insurer => insurer.name)
    }
  },
  methods: {
    ...mapActions(locationStore, ['getProfileData', 'updateProfileField', 'saveProfileData']),
    async loadProfileData() {
      if (this.launchpadFirmId) {
        await this.getProfileData(this.firm.id)
      }
    },
    changed() {
      this.$emit('changed')
    },
    async save() {
      if (this.launchpadFirmId) {
        await this.saveProfileData(this.launchpadFirmId)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-wrapper {
  width: 100%;
  position: relative;
  padding: 20px;
}

.form-label {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
}

.form-control {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  
  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

.form-text {
  font-size: 0.875em;
  color: #6c757d;
}

h5 {
  color: #495057;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.custom-multiselect {
  top: -2px;
  height: auto;
  min-height: 38px;
  padding: 0;
}
</style>
